import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '@services/admin/user.service';
import { DocumentationMmssService } from '@services/documentation-mmss.service';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { LoggerService } from '@services/logger.service';
import { UploadMmssDocumentFormMeta } from './upload-document-form-meta';
import { Registry } from '@/registry.service';
import moment from 'moment';
import { OptionSelect } from '@/models/option-select.model';

@Component({
  selector: 'app-form-upload-document',
  templateUrl: '../../../../../components/dynamic-form/dynamic-form.component.html',
})
export class FormUploadMmssDocumentComponent
  extends DynamicFormComponent
  implements OnInit, AfterViewInit
{
  encType: string = 'multipart/form-data';
  @Input() dialogRef: MatDialogRef<any>;
  @Input() modalTrigger$: Subject<any>;
  @Input() isEditMode: boolean = false;
  @Input() document: any = null;
  protected brands: OptionSelect[] = [];
  protected maggioriSconti: OptionSelect[] = [];
  protected fonteDocumentoMMSS: OptionSelect[] = [];
  protected tipologiaDocumentoMMSS: OptionSelect[] = [];
  // Oggetti completi per costruire il DTO corretto
  private brandsFull: any[] = [];
  private maggioriScontiFull: any[] = [];
  private fonteDocumentoMMSSFull: any[] = [];
  private tipologiaDocumentoMMSSFull: any[] = [];
  private file: File;

  constructor(
    protected userService: UserService,
    protected toastr: ToastrService,
    private documentationMmssService: DocumentationMmssService,
  ) {
    super(toastr, userService);
    this.submitTopButton = false;
    this.submitBottomButton = true;
    this.submitButtonVisible = true;
    this.primaryButton = 'Carica Documento';
    this.secondaryBottomButton = 'Annulla';

    Registry.getInstance().set(FormUploadMmssDocumentComponent.name, this);

    this.documentationMmssService.getBrands().subscribe((brands) => {
      this.brands = [];
      this.brandsFull = brands || [];

      for (let brand of brands) {
        this.brands.push({
          key: brand.id,
          value: brand.descrizioneMarca,
        });
      }

      this.setupMeta();
    });

    this.documentationMmssService.getSourceMmssDocuments().subscribe((fonteDocumentoMMSSs) => {
      this.fonteDocumentoMMSS = [];
      this.fonteDocumentoMMSSFull = fonteDocumentoMMSSs || [];

      for (let fonteDocumentoMMSS of fonteDocumentoMMSSs) {
        this.fonteDocumentoMMSS.push({
          key: fonteDocumentoMMSS.id,
          value: fonteDocumentoMMSS.descrizione,
        });
      }

      this.setupMeta();
    });

    this.documentationMmssService.getMmssDocumentTypes().subscribe((tipologiaDocumentoMMSSs) => {
      this.tipologiaDocumentoMMSS = [];
      this.tipologiaDocumentoMMSSFull = tipologiaDocumentoMMSSs || [];

      for (let tipologiaDocumentoMMSS of tipologiaDocumentoMMSSs) {
        this.tipologiaDocumentoMMSS.push({
          key: tipologiaDocumentoMMSS.id,
          value: tipologiaDocumentoMMSS.descrizione,
        });
      }

      this.setupMeta();
    });

    this.setupMeta();
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Aggiorna il bottone se è in modalità edit
    if (this.isEditMode) {
      this.primaryButton = 'Salva Modifiche';
    }

    // Se è in modalità edit, imposta i dati iniziali prima che il form venga creato
    if (this.isEditMode && this.document) {
      this.data = this.prepareEditData(this.document);
      // Forza il refresh del form con i nuovi dati
      this.refreshForm();
    }
  }

  ngAfterViewInit(): void {
    // Aspetta che il form sia disponibile prima di impostare il listener
    setTimeout(() => {
      this.setupMarcaListener();

      // Se è in modalità edit e c'è già una marca selezionata, carica i maggiori sconti
      if (this.isEditMode && this.form && this.document?.marca?.id) {
        const marcaControl = this.form.get('marca');
        if (marcaControl && marcaControl.value) {
          this.onMarcaChanged(marcaControl.value);
        }
      }

      // Assicura che il valore del mese sia del tipo corretto (numero) per la select
      if (this.isEditMode && this.form) {
        const meseControl = this.form.get('mese');
        if (meseControl && meseControl.value !== null && meseControl.value !== undefined) {
          const meseValue = meseControl.value;
          const meseNum = typeof meseValue === 'string' ? parseInt(meseValue, 10) : meseValue;
          if (!isNaN(meseNum) && meseNum !== meseValue) {
            meseControl.setValue(meseNum);
          }
        }
      }
    }, 0);
  }

  private setupMarcaListener(): void {
    if (!this.form) {
      // Se il form non è ancora disponibile, riprova dopo un breve delay
      setTimeout(() => {
        this.setupMarcaListener();
      }, 100);
      return;
    }

    const marcaControl = this.form.get('marca');
    if (marcaControl) {
      marcaControl.valueChanges.subscribe((brandId) => {
        this.onMarcaChanged(brandId);
      });
    } else {
      // Se il control non esiste ancora, riprova dopo un breve delay
      setTimeout(() => {
        this.setupMarcaListener();
      }, 100);
    }
  }

  private onMarcaChanged(brandId: any): void {
    const maggioreScontoControl = this.form.get('maggioreSconto');

    // Reset e disabilita il campo se marca non è selezionata
    if (!brandId) {
      if (maggioreScontoControl) {
        maggioreScontoControl.setValue(null);
        maggioreScontoControl.disable();
      }
      this.maggioriSconti = [];
      this.updateMaggioriScontiOptions();
      return;
    }

    // Carica i maggiori sconti per la marca selezionata
    this.documentationMmssService.getBigBuyersByBrand(brandId).subscribe({
      next: (bigBuyers) => {
        this.maggioriSconti = [];
        this.maggioriScontiFull = bigBuyers || [];

        for (let bigBuyer of bigBuyers) {
          this.maggioriSconti.push({
            key: bigBuyer.descrizioneMs,
            value: `${bigBuyer.codiceMs} - ${bigBuyer.descrizioneMs}`,
          });
        }

        this.updateMaggioriScontiOptions();

        // Abilita il campo dopo aver caricato le opzioni
        if (maggioreScontoControl) {
          maggioreScontoControl.enable();
        }
      },
      error: (error) => {
        LoggerService.error('getBigBuyersByBrand failed!');
        LoggerService.error(error);
        this.maggioriSconti = [];
        this.maggioriScontiFull = [];
        this.updateMaggioriScontiOptions();
        if (maggioreScontoControl) {
          maggioreScontoControl.setValue(null);
          maggioreScontoControl.disable();
        }
      },
    });
  }

  /**
   * Aggiorna solo le options del dropdown "maggioreSconto" nel meta esistente,
   * senza ricostruire l'intero array meta (che causerebbe la perdita del file caricato).
   */
  private updateMaggioriScontiOptions(): void {
    if (!this.meta || this.meta.length === 0) {
      return;
    }

    for (const metaGroup of this.meta) {
      if (metaGroup.rows) {
        for (const row of metaGroup.rows) {
          if (row.key === 'maggioreSconto') {
            (row as any).options = this.maggioriSconti;
            return;
          }
        }
      }
    }
  }

  onFileSelected(fileInput: any, index = null) {
    this.file = <File>fileInput.target.files[0];
  }

  setupMeta(): void {
    this.meta = UploadMmssDocumentFormMeta.setBrands(this.brands)
      .setFonteDocumentoMMSS(this.fonteDocumentoMMSS)
      .setMaggioriSconti(this.maggioriSconti)
      .setTipologiaDocumentoMMSS(this.tipologiaDocumentoMMSS)
      .setIsEditMode(this.isEditMode)
      .getMeta();
  }

  private prepareEditData(document: any): any {
    const editData: any = {};

    // Campi modificabili
    if (document.descrizione) {
      editData.descrizione = document.descrizione;
    }
    if (document.fonteDocumentoMMSS) {
      // Gestisce sia oggetto con id che valore diretto
      editData.fonteDocumentoMMSS = document.fonteDocumentoMMSS.id || document.fonteDocumentoMMSS;
    }
    if (document.tipologiaDocumentoMMSS) {
      // Gestisce sia oggetto con id che valore diretto
      editData.tipologiaDocumentoMMSS =
        document.tipologiaDocumentoMMSS.id || document.tipologiaDocumentoMMSS;
    }
    if (document.maggioreSconto) {
      // Gestisce sia oggetto con descrizioneMs che valore diretto
      editData.maggioreSconto = document.maggioreSconto.descrizioneMs || document.maggioreSconto;
    }
    if (document.marca) {
      // Gestisce sia oggetto con id che valore diretto (stringa come 'V', 'C', etc.)
      if (typeof document.marca === 'object' && document.marca.id) {
        editData.marca = document.marca.id;
      } else if (typeof document.marca === 'string') {
        // Se è una stringa, potrebbe essere un codice marca, ma abbiamo bisogno dell'id
        // Per ora lasciamo così, ma potrebbe essere necessario un mapping
        editData.marca = document.marca;
      }
    }
    if (document.anno) {
      // Converti l'anno in un oggetto Moment per il date picker
      // Il campo anno è di tipo yearDate che si aspetta un oggetto Moment
      const annoValue =
        typeof document.anno === 'number' ? document.anno : parseInt(document.anno, 10);
      if (!isNaN(annoValue)) {
        editData.anno = moment({ year: annoValue });
      } else {
        editData.anno = document.anno;
      }
    }
    if (document.trimestre) {
      editData.trimestre = document.trimestre;
    }
    if (document.mese) {
      // Converti il mese in numero per la select (le opzioni hanno key numeriche)
      const meseNum =
        typeof document.mese === 'string' ? parseInt(document.mese, 10) : document.mese;
      editData.mese = isNaN(meseNum) ? document.mese : meseNum;
    }

    // Campi readonly (solo per visualizzazione)
    if (document.id) {
      editData.id = document.id;
    }
    if (document.file?.name) {
      editData.fileName = document.file.name;
    }
    if (document.creator) {
      editData.creator = document.creator;
    }
    if (document.created) {
      editData.created = document.created;
    }
    if (document.modifier) {
      editData.modifier = document.modifier;
    }
    if (document.modified) {
      editData.modified = document.modified;
    }

    return editData;
  }

  onSubmit() {
    this.submitButtonLoading = true;

    // In modalità edit, il file non è obbligatorio
    if (!this.isEditMode && !this.file) {
      this.toastr.error('Seleziona un file da caricare');
      this.submitButtonLoading = false;
      Registry.getInstance().set('saveInProgress', false);
      return;
    }

    const dto: any = {};

    const idValue = this.form.get('id')?.value;
    if (idValue) {
      dto.id = idValue;
    }

    const descrizioneValue = this.form.get('descrizione')?.value;
    if (descrizioneValue) {
      dto.descrizione = String(descrizioneValue).trim();
    }

    const dettaglioValue = this.form.get('dettaglio')?.value;
    if (dettaglioValue) {
      dto.dettaglio = String(dettaglioValue).trim();
    }

    const marcaValue = this.form.get('marca')?.value;
    if (marcaValue) {
      // Cerca l'oggetto completo della marca per id
      const marcaFull = this.brandsFull.find(
        (brand) => brand.id === marcaValue || brand.id === Number(marcaValue),
      );
      if (marcaFull) {
        dto.marca = marcaFull;
      } else if (
        this.isEditMode &&
        this.document?.marca &&
        typeof this.document.marca === 'object'
      ) {
        // Fallback in edit mode: usa l'oggetto originale se disponibile
        dto.marca = this.document.marca;
      } else {
        // Fallback: se non trovato, invia solo l'id (per retrocompatibilità)
        dto.marca = String(marcaValue);
      }
    }

    const annoValue = this.form.get('anno')?.value;
    if (annoValue) {
      if (moment.isMoment(annoValue)) {
        dto.anno = annoValue.year();
      } else if (typeof annoValue === 'number') {
        dto.anno = annoValue;
      } else if (typeof annoValue === 'string') {
        const parsed = parseInt(annoValue, 10);
        if (!isNaN(parsed)) {
          dto.anno = parsed;
        }
      } else if (annoValue && typeof annoValue === 'object' && 'year' in annoValue) {
        dto.anno = (annoValue as any).year();
      }
    }

    const meseValue = this.form.get('mese')?.value;
    if (meseValue !== null && meseValue !== undefined) {
      if (typeof meseValue === 'number') {
        dto.mese = meseValue;
      } else {
        const parsed = parseInt(String(meseValue), 10);
        if (!isNaN(parsed)) {
          dto.mese = parsed;
        }
      }
    }

    const trimestreValue = this.form.get('trimestre')?.value;
    if (trimestreValue) {
      dto.trimestre = String(trimestreValue);
    }

    const maggioreScontoValue = this.form.get('maggioreSconto')?.value;
    if (maggioreScontoValue) {
      // Cerca l'oggetto completo del maggiore sconto per descrizioneMs
      // Il form usa descrizioneMs come key nelle OptionSelect
      const maggioreScontoFull = this.maggioriScontiFull.find(
        (ms) => ms.descrizioneMs === maggioreScontoValue || ms.id === maggioreScontoValue,
      );
      if (maggioreScontoFull) {
        dto.maggioreSconto = maggioreScontoFull;
      } else if (
        this.isEditMode &&
        this.document?.maggioreSconto &&
        typeof this.document.maggioreSconto === 'object'
      ) {
        // Fallback in edit mode: usa l'oggetto originale se disponibile
        dto.maggioreSconto = this.document.maggioreSconto;
      } else {
        // Fallback: se non trovato, invia solo il valore (per retrocompatibilità)
        dto.maggioreSconto = String(maggioreScontoValue);
      }
    }

    const fonteDocumentoMMSSValue = this.form.get('fonteDocumentoMMSS')?.value;
    if (fonteDocumentoMMSSValue) {
      // Cerca l'oggetto completo della fonte per id
      const fonteFull = this.fonteDocumentoMMSSFull.find(
        (fonte) =>
          fonte.id === fonteDocumentoMMSSValue || fonte.id === Number(fonteDocumentoMMSSValue),
      );
      if (fonteFull) {
        dto.fonteDocumentoMMSS = fonteFull;
      } else if (
        this.isEditMode &&
        this.document?.fonteDocumentoMMSS &&
        typeof this.document.fonteDocumentoMMSS === 'object'
      ) {
        // Fallback in edit mode: usa l'oggetto originale se disponibile
        dto.fonteDocumentoMMSS = this.document.fonteDocumentoMMSS;
      } else {
        // Fallback: se non trovato, invia solo l'id (per retrocompatibilità)
        dto.fonteDocumentoMMSS = String(fonteDocumentoMMSSValue);
      }
    }

    const tipologiaDocumentoMMSSValue = this.form.get('tipologiaDocumentoMMSS')?.value;
    if (tipologiaDocumentoMMSSValue) {
      // Cerca l'oggetto completo della tipologia per id
      const tipologiaFull = this.tipologiaDocumentoMMSSFull.find(
        (tipologia) =>
          tipologia.id === tipologiaDocumentoMMSSValue ||
          tipologia.id === Number(tipologiaDocumentoMMSSValue),
      );
      if (tipologiaFull) {
        dto.tipologiaDocumentoMMSS = tipologiaFull;
      } else if (
        this.isEditMode &&
        this.document?.tipologiaDocumentoMMSS &&
        typeof this.document.tipologiaDocumentoMMSS === 'object'
      ) {
        // Fallback in edit mode: usa l'oggetto originale se disponibile
        dto.tipologiaDocumentoMMSS = this.document.tipologiaDocumentoMMSS;
      } else {
        // Fallback: se non trovato, invia solo l'id (per retrocompatibilità)
        dto.tipologiaDocumentoMMSS = String(tipologiaDocumentoMMSSValue);
      }
    }

    LoggerService.log('DTO to send:', dto);
    LoggerService.log('DTO JSON:', JSON.stringify(dto));

    if (this.isEditMode && this.document) {
      // Modalità edit: chiama update
      this.documentationMmssService.updateDocument(this.document.id, dto).subscribe({
        next: (response) => {
          this.toastr.success('Documento modificato con successo!');
          Registry.getInstance().set('saveInProgress', false);
          this.bottomButtonAction();
        },
        error: (error) => {
          this.toastr.error('Errore durante la modifica del documento!');
          LoggerService.error('updateDocument failed!');
          LoggerService.error(error);
          this.submitButtonLoading = false;
          Registry.getInstance().set('saveInProgress', false);
        },
      });
    } else {
      // Modalità upload: chiama upload
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('dto', new Blob([JSON.stringify(dto)], { type: 'application/json' }));

      this.documentationMmssService.uploadDocument(formData).subscribe({
        next: (response) => {
          this.toastr.success('Documento caricato con successo!');
          Registry.getInstance().set('saveInProgress', false);
          this.bottomButtonAction();
        },
        error: (error) => {
          this.toastr.error('Errore durante il caricamento del documento!');
          LoggerService.error('uploadDocument failed!');
          LoggerService.error(error);
          this.submitButtonLoading = false;
          Registry.getInstance().set('saveInProgress', false);
        },
      });
    }
  }

  bottomButtonAction() {
    this.submitButtonLoading = false;
    if (this.modalTrigger$) {
      this.modalTrigger$.next();
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
